// Calendar (manager): per academic year the preschool's term halves, half terms and holidays with official and
// actual dates, bank holidays and closures, and the open weeks invoicing uses -- together with the Holiday Club days
// (from the Holiday Club forms) and how many children are booked on each.

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { Link } from "react-router-dom";
import { AppContext } from "../Store";
import { useSnackbar } from "../shared/SnackbarProvider";
import { StatStrip } from "../shared/StatStrip";
import { TermDatesDialog } from "./holidayClub/TermDatesDialog";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import {
  saveTermDates,
  termDayDates,
  watchRegistrations as watchHolidayRegistrations,
  watchTermDates,
  watchTerms as watchHolidayTerms,
  type HolidayRegistration,
  type HolidayTerm,
} from "../shared/holidayClub";
import {
  BANK_HOLIDAYS,
  PERIODS,
  academicYearId,
  closedDays,
  clubDaysIn,
  datesBetween,
  emptyCalendar,
  openDays,
  saveCalendar,
  termPeriodKeys,
  today,
  watchCalendars,
  weekdayCount,
  weekdayOf,
  weeksAndDays,
  type CalendarPeriod,
  type DateRange,
  type PreschoolCalendar,
} from "../shared/preschoolInvoices";

const yearLabel = (id: string) => `${id.slice(0, 4)}/${id.slice(5)}`;
const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const shift = (iso: string, days: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** A new year's draft: last year's dates moved on 52 weeks (same weekdays), to correct against the official list. */
const nextYearDraft = (previous: PreschoolCalendar | undefined, id: string): PreschoolCalendar => {
  if (!previous) return emptyCalendar(id);
  const move = (range: DateRange | null) => (range ? { start: shift(range.start, 364), end: shift(range.end, 364) } : null);
  return { id, periods: previous.periods.map((p) => ({ ...p, official: move(p.official), actual: move(p.actual), club: move(p.club ?? null) })), closures: [], openOnBankHolidays: [] };
};

interface Issue {
  /** The row it belongs to: a period key, or "club:<period key>" for a Holiday Club row. */
  row: string;
  level: "error" | "warning";
  message: string;
}

const half = (range: DateRange | null | undefined) => !!range && (!range.start || !range.end);
const backwards = (range: DateRange | null | undefined) => !!range && !!range.start && !!range.end && range.end < range.start;

/**
 * What is wrong with the dates as entered. Errors (block saving): a date range ending before it starts or half
 * filled, periods overlapping or out of order, a Holiday Club outside its school holiday. Warnings: missing dates,
 * weekdays between two periods that belong to neither.
 */
const issuesOf = (calendar: PreschoolCalendar): Issue[] => {
  const issues: Issue[] = [];
  let previous: { name: string; end: string } | null = null;
  for (const period of calendar.periods) {
    const def = PERIODS.find((p) => p.key === period.key)!;
    const add = (level: Issue["level"], message: string, row: string = period.key) => issues.push({ row, level, message });
    if (half(period.official) || backwards(period.official)) add("error", "Official dates: the end must be on or after the start.");
    const range = period.actual;
    if (!range || !range.start || !range.end) {
      add(half(range) ? "error" : "warning", half(range) ? "Actual dates: set both start and end." : "Actual dates missing.");
    } else if (backwards(range)) {
      add("error", "Actual dates: the end is before the start.");
    } else {
      if (previous && range.start <= previous.end) {
        add("error", `Overlaps ${previous.name} (which ends ${shortDate(previous.end)}).`);
      } else if (previous) {
        const gap = datesBetween({ start: previous.end, end: range.start }).slice(1, -1).filter((d) => weekdayOf(d) < 5 && !BANK_HOLIDAYS[d]);
        if (gap.length) {
          const days = gap.length <= 3 ? gap.map(shortDate).join(", ") : `${gap.length} weekdays`;
          add("warning", `${days} ${gap.length === 1 ? "is" : "are"} in neither ${previous.name} nor ${def.name}.`);
        }
      }
      previous = { name: def.name, end: range.end };
    }
    if (!def.term && period.club) {
      const club = period.club;
      const row = `club:${period.key}`;
      if (half(club)) add("error", "Holiday Club: set both start and end.", row);
      else if (backwards(club)) add("error", "Holiday Club: the end is before the start.", row);
      else if (range && range.start && range.end && (club.start < range.start || club.end > range.end)) {
        add("error", `Holiday Club runs outside the ${def.name} (${shortDate(range.start)} – ${shortDate(range.end)}).`, row);
      }
    }
  }
  return issues;
};

const IssueLine: React.FC<{ issues: Issue[] }> = ({ issues }) => (
  issues.length ? (
    <Stack sx={{ gridColumn: "1 / -1", pt: 0.5 }} spacing={0.25}>
      {issues.map((issue) => (
        <Stack key={issue.message} direction="row" spacing={0.75} alignItems="center">
          {issue.level === "error" ? <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} /> : <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />}
          <Typography variant="caption" color={issue.level === "error" ? "error.main" : "warning.dark"}>{issue.message}</Typography>
        </Stack>
      ))}
    </Stack>
  ) : null
);

const DateCell: React.FC<{ value: string | undefined; onChange: (value: string) => void; label: string }> = ({ value, onChange, label }) => (
  <TextField type="date" size="small" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    inputProps={{ "aria-label": label }} sx={{ "& input": { py: 0.75, fontSize: 14 } }} fullWidth />
);

const GRID = "minmax(140px, 1.2fr) repeat(4, minmax(130px, 1fr)) minmax(120px, 0.9fr)";

type DayKind = "open" | "holiday" | "closed" | "bank" | "club" | "none";

/** Colours shared by the ribbon, the month grids and the legend. */
const useDayColours = () => {
  const theme = useTheme();
  return {
    open: alpha(theme.palette.success.main, 0.28),
    holiday: alpha("#f4a261", 0.35),
    closed: alpha(theme.palette.error.main, 0.4),
    bank: alpha("#7e57c2", 0.45),
    club: alpha(theme.palette.info.main, 0.45),
    none: "transparent",
  };
};

/** A Holiday Club day and its bookings. */
interface ClubDay { name: string; full: number; half: number }

const MonthGrid: React.FC<{ month: string; kindOf: (date: string) => DayKind; reasonOf: (date: string) => string }> = ({ month, kindOf, reasonOf }) => {
  const theme = useTheme();
  const first = `${month}-01`;
  const start = new Date(`${first}T12:00:00Z`);
  const days = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)).getUTCDate();
  const offset = weekdayOf(first);
  const colours = useDayColours();
  return (
    <Box>
      <Typography variant="caption" fontWeight={600}>{start.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", mt: 0.5 }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <Typography key={i} variant="caption" color="text.disabled" textAlign="center" sx={{ fontSize: 10 }}>{d}</Typography>
        ))}
        {Array.from({ length: offset }).map((_, i) => <span key={`pad${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const date = `${month}-${String(i + 1).padStart(2, "0")}`;
          const kind = kindOf(date);
          const reason = reasonOf(date);
          const cell = (
            <Box sx={{
              aspectRatio: "1", borderRadius: 0.75, display: "grid", placeItems: "center", fontSize: 11,
              bgcolor: colours[kind], color: weekdayOf(date) > 4 ? "text.disabled" : "text.primary",
              outline: date === today() ? `2px solid ${theme.palette.primary.main}` : "none",
            }}>{i + 1}</Box>
          );
          return reason ? <Tooltip key={date} title={reason}>{cell}</Tooltip> : <React.Fragment key={date}>{cell}</React.Fragment>;
        })}
      </Box>
    </Box>
  );
};

/** The academic year as one strip: preschool terms, half terms and holidays, and Holiday Club days below. */
const YearRibbon: React.FC<{ calendar: PreschoolCalendar; clubDays: Map<string, ClubDay> }> = ({ calendar, clubDays }) => {
  const theme = useTheme();
  const colours = useDayColours();
  const startYear = parseInt(calendar.id.slice(0, 4), 10);
  const first = Date.parse(`${startYear}-09-01T12:00:00Z`);
  const total = (Date.parse(`${startYear + 1}-08-31T12:00:00Z`) - first) / 86400000 + 1;
  const pos = (iso: string) => ((Date.parse(`${iso}T12:00:00Z`) - first) / 86400000 / total) * 100;
  const now = today();
  const short: Record<string, string> = {
    autumn1: "Autumn 1", autumnHalf: "Half term", autumn2: "Autumn 2", christmas: "Christmas",
    spring1: "Spring 1", springHalf: "Half term", spring2: "Spring 2", easter: "Easter",
    summer1: "Summer 1", summerHalf: "Half term", summer2: "Summer 2", summerHoliday: "Summer holiday",
  };
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ position: "relative", height: 34, borderRadius: 1.5, overflow: "hidden", bgcolor: theme.palette.action.hover }}>
        {calendar.periods.filter((p) => p.actual && p.actual.start <= p.actual.end).map((p) => {
          const def = PERIODS.find((d) => d.key === p.key)!;
          const left = Math.max(0, pos(p.actual!.start));
          const width = Math.min(100, pos(p.actual!.end) + 100 / total) - left;
          return (
            <Tooltip key={p.key} title={`${def.name}: ${shortDate(p.actual!.start)} – ${shortDate(p.actual!.end)}`}>
              <Box sx={{
                position: "absolute", top: 0, bottom: 0, left: `${left}%`, width: `${width}%`,
                bgcolor: def.term ? colours.open : colours.holiday, borderRight: `1px solid ${theme.palette.background.paper}`,
                display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
              }}>
                {width > 7 && (
                  <Typography sx={{ fontSize: 11, fontWeight: def.term ? 700 : 500 }} color={def.term ? "success.dark" : "#9a5a1f"} noWrap>
                    {short[p.key]}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
        {now >= `${startYear}-09-01` && now <= `${startYear + 1}-08-31` && (
          <Tooltip title={`Today, ${shortDate(now)}`}>
            <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${pos(now)}%`, width: 2, bgcolor: "primary.main" }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ position: "relative", height: 12, mt: 0.5 }}>
        {[...clubDays.entries()].filter(([date]) => pos(date) >= 0 && pos(date) <= 100).map(([date, day]) => (
          <Tooltip key={date} title={`Holiday Club · ${shortDate(date)} · ${day.full} full, ${day.half} half`}>
            <Box sx={{ position: "absolute", top: 0, height: 12, left: `${pos(date)}%`, width: `max(3px, ${100 / total}%)`, borderRadius: 0.5, bgcolor: "info.main" }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ position: "relative", height: 16, mt: 0.25 }}>
        {Array.from({ length: 12 }, (_, i) => {
          const m = (8 + i) % 12;
          const iso = `${m < 8 ? startYear + 1 : startYear}-${String(m + 1).padStart(2, "0")}-01`;
          return (
            <Typography key={iso} variant="caption" color="text.secondary" sx={{ position: "absolute", left: `${pos(iso)}%`, fontSize: 10 }}>
              {new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { month: "short" })}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};

const PreschoolCalendarPage: React.FC = () => {
  const { userContext } = useContext(AppContext);
  const snack = useSnackbar();
  const theme = useTheme();
  const [calendars, setCalendars] = useState<PreschoolCalendar[] | null>(null);
  const [yearId, setYearId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PreschoolCalendar | null>(null);
  const [saving, setSaving] = useState(false);
  const [newClosure, setNewClosure] = useState({ date: "", reason: "" });
  const [holidayTerms, setHolidayTerms] = useState<HolidayTerm[]>([]);
  const [holidayRegs, setHolidayRegs] = useState<Record<string, HolidayRegistration[]>>({});
  const [termDates, setTermDates] = useState<{ [termId: string]: { [label: string]: string } }>({});
  const colours = useDayColours();

  useEffect(() => watchCalendars((loaded) => {
    setCalendars(loaded);
    setYearId((current) => {
      if (current) return current;
      const now = today();
      const thisYear = academicYearId(parseInt(now.slice(0, 4), 10) - (parseInt(now.slice(5, 7), 10) < 8 ? 1 : 0));
      return loaded.some((c) => c.id === thisYear) ? thisYear : loaded.map((c) => c.id).sort().pop() ?? thisYear;
    });
  }, (error) => snack(`Could not load the calendar: ${error.message}`, "error")), []);

  useEffect(() => watchHolidayTerms(setHolidayTerms, (error) => console.warn("Holiday terms not loaded", error)), []);
  useEffect(() => watchTermDates(setTermDates, (error) => console.warn("Holiday date corrections not loaded", error)), []);

  // Bookings of the Holiday Club terms with days in this academic year.
  const yearStart = yearId ? `${yearId.slice(0, 4)}-09-01` : "";
  const yearEnd = yearId ? `${parseInt(yearId.slice(0, 4), 10) + 1}-08-31` : "";
  // Club days with the manager's corrections, the labels read again (typos fixed) otherwise.
  const clubDates = useMemo(() => new Map(holidayTerms.map((t) => [t.id, termDayDates(t, termDates[t.id])])), [holidayTerms, termDates]);
  const clubTerms = useMemo(() => holidayTerms.filter((t) => (clubDates.get(t.id) ?? []).some((d) => d.date && d.date >= yearStart && d.date <= yearEnd)),
    [holidayTerms, clubDates, yearStart, yearEnd]);
  const clubTermIds = clubTerms.map((t) => t.id).join(",");
  useEffect(() => {
    const stops = clubTerms.map((t) => watchHolidayRegistrations(t.id,
      (regs) => setHolidayRegs((all) => ({ ...all, [t.id]: regs })), (error) => console.warn("Holiday bookings not loaded", error)));
    return () => stops.forEach((stop) => stop());
  }, [clubTermIds]);
  const clubDays = useMemo(() => {
    const days = new Map<string, ClubDay>();
    for (const term of clubTerms) {
      const name = term.name.replace(/^Registration form\s*-\s*/i, "");
      const dateOfLabel = new Map((clubDates.get(term.id) ?? []).map((d) => [d.label, d.date]));
      for (const date of dateOfLabel.values()) if (date) days.set(date, { name, full: 0, half: 0 });
      for (const reg of holidayRegs[term.id] ?? []) {
        if (reg.removedFromForm || reg.admin?.cancelled) continue;
        for (const day of reg.form.days) {
          const date = dateOfLabel.get(day.label);
          const entry = date ? days.get(date) : undefined;
          if (entry && day.choice === "FULL") entry.full++;
          if (entry && day.choice === "HALF") entry.half++;
        }
      }
    }
    return days;
  }, [clubTerms, clubDates, holidayRegs]);

  // One row per club, after the period its first day falls in (October club after the autumn half term, ...).
  const [editingClub, setEditingClub] = useState<HolidayTerm | null>(null);
  const clubRows = useMemo(() => clubTerms.map((term) => {
    const dates = (clubDates.get(term.id) ?? []).map((d) => d.date).filter((d): d is string => !!d).sort();
    const inYear = dates.filter((d) => d >= yearStart && d <= yearEnd);
    const first = inYear[0];
    const bookings = inYear.reduce((sum, d) => sum + (clubDays.get(d)?.full ?? 0) + (clubDays.get(d)?.half ?? 0), 0);
    return { term, name: term.name.replace(/^Registration form\s*-\s*/i, ""), first, last: inYear[inYear.length - 1], days: inYear.length, bookings };
  }).filter((club) => club.first), [clubTerms, clubDates, clubDays, yearStart, yearEnd]);

  const saved = calendars?.find((c) => c.id === yearId) ?? null;
  useEffect(() => {
    if (!yearId || !calendars) return;
    setDraft(saved ? JSON.parse(JSON.stringify(saved)) : nextYearDraft(calendars.find((c) => c.id === academicYearId(parseInt(yearId, 10) - 1)), yearId));
  }, [yearId, saved]);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const closed = useMemo(() => (draft ? closedDays(draft) : []), [draft]);

  if (!import.meta.env.DEV && !userContext?.isAdmin()) {
    return <Box sx={{ padding: 2 }}><Alert severity="warning">Only the manager can manage the calendar.</Alert></Box>;
  }
  if (!calendars || !draft || !yearId) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const years = [...new Set([...calendars.map((c) => c.id), yearId])].sort().reverse();
  const nextId = academicYearId(parseInt(years[0], 10) + 1);
  const setPeriod = (key: string, which: "official" | "actual", field: "start" | "end", value: string) =>
    setDraft({
      ...draft,
      periods: draft.periods.map((p) => {
        if (p.key !== key) return p;
        const range = p[which] ?? { start: "", end: "" };
        const next = { ...range, [field]: value };
        return { ...p, [which]: next.start || next.end ? next : null };
      }),
    });

  const setClub = (key: string, field: "start" | "end", value: string, fallback: DateRange | null) =>
    setDraft({
      ...draft,
      periods: draft.periods.map((p) => {
        if (p.key !== key) return p;
        const next = { ...(p.club ?? fallback ?? { start: "", end: "" }), [field]: value };
        return { ...p, club: next.start || next.end ? next : null };
      }),
    });

  const termTotal = (term: "autumn" | "spring" | "summer") => openDays(draft, termPeriodKeys(term)).length;
  const yearOpen = (["autumn", "spring", "summer"] as const).reduce((sum, t) => sum + termTotal(t), 0);
  // A club form belongs to the school holiday its first day falls in; one outside every holiday gets a row of its own
  // after the last period that starts before it.
  const holidayOf = (date: string) => draft.periods.find((p) => !PERIODS.find((d) => d.key === p.key)!.term
    && p.actual && date >= p.actual.start && date <= p.actual.end);
  const formClubsByHoliday = new Map<string, typeof clubRows>();
  const placedClubRows: (typeof clubRows[number] & { afterPeriod: string })[] = [];
  for (const club of clubRows) {
    const holiday = holidayOf(club.first);
    if (holiday) {
      formClubsByHoliday.set(holiday.key, [...(formClubsByHoliday.get(holiday.key) ?? []), club]);
    } else {
      const before = draft.periods.filter((p) => p.actual && p.actual.start <= club.first);
      placedClubRows.push({ ...club, afterPeriod: (before[before.length - 1] ?? draft.periods[0]).key });
    }
  }
  // Planned club days (no form yet) for the ribbon and month grids.
  const allClubDays = new Map(clubDays);
  for (const period of draft.periods) {
    if (formClubsByHoliday.has(period.key)) continue;
    for (const date of clubDaysIn(period.club)) if (!allClubDays.has(date)) allClubDays.set(date, { name: "planned", full: 0, half: 0 });
  }

  // Year at a glance: classify every day once.
  const kinds = new Map<string, DayKind>();
  const reasons = new Map<string, string>();
  for (const period of draft.periods) {
    const def = PERIODS.find((p) => p.key === period.key)!;
    if (!period.actual || period.actual.end < period.actual.start) continue;
    for (const date of datesBetween(period.actual)) {
      if (weekdayOf(date) > 4) continue;
      kinds.set(date, def.term ? "open" : "holiday");
      if (!def.term) reasons.set(date, def.name);
    }
  }
  for (const [date, day] of allClubDays) {
    kinds.set(date, "club");
    reasons.set(date, day.name === "planned" ? "Holiday Club (planned, no form yet)" : `Holiday Club (${day.name}) · ${day.full} full, ${day.half} half`);
  }
  for (const closure of draft.closures) { kinds.set(closure.date, "closed"); reasons.set(closure.date, closure.reason || "Closed"); }
  for (const date of Object.keys(BANK_HOLIDAYS)) {
    if (date < yearStart || date > yearEnd || weekdayOf(date) > 4) continue;
    kinds.set(date, "bank");
    reasons.set(date, `${BANK_HOLIDAYS[date]} · bank holiday`);
  }
  const startYear = parseInt(yearId.slice(0, 4), 10);
  const months = Array.from({ length: 12 }, (_, i) => {
    const m = (8 + i) % 12; // September first
    return `${m < 8 ? startYear + 1 : startYear}-${String(m + 1).padStart(2, "0")}`;
  });

  const bankHolidays = Object.keys(BANK_HOLIDAYS).filter((date) => date >= yearStart && date <= yearEnd && weekdayOf(date) < 5).sort();
  const inTerm = (date: string) => draft.periods.some((p) => PERIODS.find((d) => d.key === p.key)!.term && p.actual
    && date >= p.actual.start && date <= p.actual.end);
  const issues = issuesOf(draft);
  const errors = issues.filter((i) => i.level === "error");
  const warnings = issues.filter((i) => i.level === "warning");
  const issuesFor = (row: string) => issues.filter((i) => i.row === row);
  const rowBorder = (row: string) => {
    const list = issuesFor(row);
    if (list.some((i) => i.level === "error")) return `inset 3px 0 0 ${theme.palette.error.main}`;
    if (list.length) return `inset 3px 0 0 ${theme.palette.warning.main}`;
    return undefined;
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveCalendar(draft);
      snack(`Calendar ${yearLabel(draft.id)} saved`, "success");
    } catch (error) {
      snack(`Could not save: ${error instanceof Error ? error.message : String(error)}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, maxWidth: 1280, mx: "auto", pb: 12 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={600}>Calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            Preschool terms and Holiday Club. Actual dates drive <Link to="/preschool-invoices">preschool invoices</Link>;
            club days come from the <Link to="/holiday-club">Holiday Club</Link> forms.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Academic year</InputLabel>
          <Select label="Academic year" value={yearId} onChange={(e) => setYearId(e.target.value)}>
            {years.map((id) => (
              <MenuItem key={id} value={id}>{yearLabel(id)}{calendars.some((c) => c.id === id) ? "" : " (new)"}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setYearId(nextId)}>Add {yearLabel(nextId)}</Button>
      </Stack>

      {!saved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          New year: dates are last year's moved on 52 weeks. Correct them against the official term dates, then save.
        </Alert>
      )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>Year at a glance</Typography>
            <Chip size="small" label="Preschool open" sx={{ bgcolor: colours.open }} />
            <Chip size="small" label="Holiday Club" sx={{ bgcolor: colours.club }} />
            <Chip size="small" label="School holidays" sx={{ bgcolor: colours.holiday }} />
            <Chip size="small" label="Bank holiday" sx={{ bgcolor: colours.bank }} />
            <Chip size="small" label="Closed" sx={{ bgcolor: colours.closed }} />
          </Stack>
          <YearRibbon calendar={draft} clubDays={allClubDays} />
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(6, 1fr)" } }}>
            {months.map((month) => (
              <MonthGrid key={month} month={month} kindOf={(d) => kinds.get(d) ?? "none"} reasonOf={(d) => reasons.get(d) ?? ""} />
            ))}
          </Box>
        </Paper>

      <StatStrip minCell={150} items={[
        ...(["autumn", "spring", "summer"] as const).map((term) => ({
          label: `${term} term`, color: "success" as const, value: weeksAndDays(termTotal(term)), hint: "preschool open",
        })),
        { label: "Preschool year", color: "primary" as const, value: weeksAndDays(yearOpen),
          hint: `${closed.length} closure day${closed.length === 1 ? "" : "s"} in term` },
        { label: "Holiday Club", color: "info" as const, value: `${allClubDays.size} day${allClubDays.size === 1 ? "" : "s"}`,
          hint: clubDays.size
            ? `${[...clubDays.values()].reduce((sum, d) => sum + d.full + d.half, 0)} bookings · ${clubTerms.length} club${clubTerms.length === 1 ? "" : "s"}`
            : "no club forms this year" },
      ]} />

      {issues.length > 0 && (
        <Alert severity={errors.length ? "error" : "warning"} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {[errors.length && `${errors.length} error${errors.length === 1 ? "" : "s"}`, warnings.length && `${warnings.length} warning${warnings.length === 1 ? "" : "s"}`]
              .filter(Boolean).join(" · ")}
            {errors.length ? " — fix the rows marked in red to save" : ""}
          </Typography>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2, overflowX: "auto" }}>
        <Box sx={{ minWidth: 820 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID, columnGap: 1.5, px: 2, py: 1, typography: "caption", fontWeight: 600, color: "text.secondary", textTransform: "uppercase" }}>
            <span>Period</span><span>Official start</span><span>Official end</span><span>Actual start</span><span>Actual end</span><Box textAlign="right">Open</Box>
          </Box>
          {draft.periods.map((period: CalendarPeriod) => {
            const def = PERIODS.find((p) => p.key === period.key)!;
            const open = def.term ? openDays(draft, [period.key]).length : 0;
            const weekdays = weekdayCount(period.actual);
            const lost = def.term ? weekdays - open : 0;
            const isTermEnd = def.key === "autumn2" || def.key === "spring2" || def.key === "summer2";
            return (
              <React.Fragment key={period.key}>
                <Box sx={{
                  display: "grid", gridTemplateColumns: GRID, columnGap: 1.5, alignItems: "center", px: 2, py: 0.75,
                  borderTop: 1, borderColor: "divider", boxShadow: rowBorder(period.key),
                  bgcolor: def.term ? alpha(theme.palette.success.main, 0.05) : alpha("#f4a261", 0.07),
                }}>
                  <Typography fontWeight={def.term ? 600 : 400} color={def.term ? "text.primary" : "text.secondary"}>{def.name}</Typography>
                  <DateCell label={`${def.name} official start`} value={period.official?.start} onChange={(v) => setPeriod(period.key, "official", "start", v)} />
                  <DateCell label={`${def.name} official end`} value={period.official?.end} onChange={(v) => setPeriod(period.key, "official", "end", v)} />
                  <DateCell label={`${def.name} actual start`} value={period.actual?.start} onChange={(v) => setPeriod(period.key, "actual", "start", v)} />
                  <DateCell label={`${def.name} actual end`} value={period.actual?.end} onChange={(v) => setPeriod(period.key, "actual", "end", v)} />
                  <Box textAlign="right">
                    {def.term ? (
                      <>
                        <Typography variant="body2" fontWeight={600}>{weeksAndDays(open)}</Typography>
                        {lost > 0 && (() => {
                          const days = closed.filter((d) => period.actual && d.date >= period.actual.start && d.date <= period.actual.end);
                          const banks = days.filter((d) => d.bankHoliday).length;
                          const label = banks === days.length
                            ? `${banks} bank holiday${banks === 1 ? "" : "s"}`
                            : `${days.length} day${days.length === 1 ? "" : "s"} closed`;
                          return (
                            <Tooltip title={days.map((d) => `${shortDate(d.date)}: ${d.reason}`).join(" · ")}>
                              <Chip size="small" variant="outlined" icon={<EventBusyIcon />} label={`${label}, not counted`}
                                sx={{ height: 20, fontSize: 11, mt: 0.25, "& .MuiChip-icon": { fontSize: 14 } }} />
                            </Tooltip>
                          );
                        })()}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{period.actual ? `closed ${weeksAndDays(weekdays)}` : "—"}</Typography>
                    )}
                  </Box>
                  <IssueLine issues={issuesFor(period.key)} />
                </Box>
                {!def.term && (() => {
                  const forms = formClubsByHoliday.get(period.key) ?? [];
                  const formSpan = forms.length ? { start: forms[0].first, end: forms[forms.length - 1].last } : null;
                  const range = period.club ?? formSpan;
                  const days = forms.length ? forms.reduce((sum, f) => sum + f.days, 0) : clubDaysIn(range).length;
                  const bookings = forms.reduce((sum, f) => sum + f.bookings, 0);
                  return (
                    <Box sx={{
                      display: "grid", gridTemplateColumns: GRID, columnGap: 1.5, alignItems: "center", px: 2, py: 0.75,
                      borderTop: 1, borderColor: "divider", bgcolor: alpha(theme.palette.info.main, range ? 0.08 : 0.03),
                      boxShadow: rowBorder(`club:${period.key}`) ?? `inset 3px 0 0 ${theme.palette.info.main}`,
                    }}>
                      <Box sx={{ minWidth: 0, pl: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} color="info.dark" noWrap>Holiday Club</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {forms.length ? forms.map((f) => f.name).join(", ") : range ? "no form yet" : "not planned"}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.disabled" sx={{ gridColumn: "span 2" }}>
                        {forms.length && !period.club ? "dates from the club's form" : ""}
                      </Typography>
                      <DateCell label={`${def.name} club start`} value={range?.start} onChange={(v) => setClub(period.key, "start", v, formSpan)} />
                      <DateCell label={`${def.name} club end`} value={range?.end} onChange={(v) => setClub(period.key, "end", v, formSpan)} />
                      <Box textAlign="right">
                        {range ? (
                          <Typography variant="body2" fontWeight={600} color="info.dark">{days} club day{days === 1 ? "" : "s"}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                        {forms.length > 0 && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <Typography variant="caption" color="text.secondary">{bookings} bookings</Typography>
                            <Tooltip title="Correct the days on the club's form">
                              <IconButton size="small" onClick={() => setEditingClub(forms[0].term)}><EditCalendarIcon sx={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </Box>
                      <IssueLine issues={issuesFor(`club:${period.key}`)} />
                    </Box>
                  );
                })()}
                {placedClubRows.filter((club) => club.afterPeriod === period.key).map((club) => (
                  <Box key={club.term.id} sx={{
                    display: "grid", gridTemplateColumns: GRID, columnGap: 1.5, alignItems: "center", px: 2, py: 0.75,
                    borderTop: 1, borderColor: "divider", bgcolor: alpha(theme.palette.info.main, 0.08),
                    boxShadow: `inset 3px 0 0 ${theme.palette.info.main}`,
                  }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={600} color="info.dark" noWrap>Holiday Club</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">{club.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.disabled" sx={{ gridColumn: "span 2" }}>from the club's form</Typography>
                    <Typography variant="body2">{shortDate(club.first)}</Typography>
                    <Typography variant="body2">{shortDate(club.last)}</Typography>
                    <Box textAlign="right">
                      <Typography variant="body2" fontWeight={600} color="info.dark">{club.days} day{club.days === 1 ? "" : "s"}</Typography>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        <Typography variant="caption" color="text.secondary">{club.bookings} bookings</Typography>
                        <Tooltip title="Edit the club's dates">
                          <IconButton size="small" onClick={() => setEditingClub(club.term)}><EditCalendarIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Box>
                ))}
                {isTermEnd && (
                  <Box sx={{ px: 2, py: 0.5, textAlign: "right", bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <Typography variant="body2" fontWeight={600}>
                      {def.name.split(" ")[0]} term total: {weeksAndDays(termTotal(def.term!))}
                    </Typography>
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>

      <Box sx={{ maxWidth: 560 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Bank holidays and closures</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Always closed; days in term are taken off every child's hours.</Typography>
          <Stack spacing={1}>
            {bankHolidays.map((date) => (
              <Stack key={date} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colours.bank, flexShrink: 0 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>{shortDate(date)}</Typography>
                  <Typography variant="caption" color="text.secondary">{BANK_HOLIDAYS[date]}{inTerm(date) ? " · in term, not counted" : ""}</Typography>
                </Box>
              </Stack>
            ))}
            {draft.closures.slice().sort((a, b) => a.date.localeCompare(b.date)).map((closure) => (
              <Stack key={closure.date} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>{shortDate(closure.date)}</Typography>
                  <Typography variant="caption" color="text.secondary">{closure.reason || "Closed"}</Typography>
                </Box>
                <IconButton size="small" aria-label="Remove closure"
                  onClick={() => setDraft({ ...draft, closures: draft.closures.filter((c) => c.date !== closure.date) })}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            {draft.closures.length === 0 && (
              <Typography variant="body2" color="text.secondary">No closures of your own.</Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
              <TextField type="date" size="small" label="Date" value={newClosure.date} InputLabelProps={{ shrink: true }}
                onChange={(e) => setNewClosure({ ...newClosure, date: e.target.value })} sx={{ width: 160 }} />
              <TextField size="small" label="Reason" placeholder="INSET day" value={newClosure.reason}
                onChange={(e) => setNewClosure({ ...newClosure, reason: e.target.value })} sx={{ flex: 1 }} />
              <IconButton aria-label="Add closure" color="primary"
                disabled={!newClosure.date || draft.closures.some((c) => c.date === newClosure.date)}
                onClick={() => {
                  setDraft({ ...draft, closures: [...draft.closures, { date: newClosure.date, reason: newClosure.reason.trim() || "Closed" }] });
                  setNewClosure({ date: "", reason: "" });
                }}>
                <AddIcon />
              </IconButton>
            </Stack>
            {newClosure.date && weekdayOf(newClosure.date) > 4 && <Typography variant="caption" color="warning.main">That is a weekend.</Typography>}
          </Stack>
        </Paper>

      </Box>

      <TermDatesDialog term={editingClub} corrections={editingClub ? termDates[editingClub.id] ?? {} : {}}
        onClose={() => setEditingClub(null)}
        onSave={async (dates) => {
          try {
            await saveTermDates(editingClub!.id, dates);
            snack("Club dates saved", "success");
          } catch (error) {
            snack(`Could not save dates: ${error instanceof Error ? error.message : String(error)}`, "error");
          }
        }} />

      {dirty && (
        <Paper elevation={6} sx={{
          position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", px: 2, py: 1.25, zIndex: 10,
          display: "flex", alignItems: "center", gap: 2, borderRadius: 3,
        }}>
          <Typography variant="body2">Unsaved changes to {yearLabel(draft.id)}</Typography>
          <Button onClick={() => setDraft(saved ? JSON.parse(JSON.stringify(saved)) : emptyCalendar(draft.id))} disabled={saving}>Discard</Button>
          {errors.length > 0 && <Typography variant="body2" color="error.main">{errors.length} error{errors.length === 1 ? "" : "s"} to fix</Typography>}
          <Button variant="contained" onClick={save} disabled={saving || errors.length > 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Save
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default PreschoolCalendarPage;
